import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Inject Google Font for Półtawski Nowy
const fontStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Pooltawski+Nowy:ital,wght@0,400;0,600;0,700;1,400&display=swap');

  /* Fallback: use a serif with similar character */
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap');

  :root {
    --russian-red: #CD0302;
    --sunburst: #F0C400;
    --edam: #F5D19D;
    --citrusy-orange: #F88702;
    --autumn-green: #4F7E17;
    --cream: #FEFBF3;
    --dark-green: #2C4A0E;
    --light-edam: #FDF3E3;
  }

  * { box-sizing: border-box; }

  .organic-page {
    font-family: 'Półtawski Nowy', 'Playfair Display', Georgia, serif;
    background-color: var(--cream);
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* Organic background texture */
  .organic-page::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image:
      radial-gradient(ellipse at 10% 20%, rgba(79,126,23,0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 90% 80%, rgba(205,3,2,0.06) 0%, transparent 50%),
      radial-gradient(ellipse at 50% 50%, rgba(240,196,0,0.05) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  /* Leaf decorative elements */
  .leaf-decoration {
    position: absolute;
    opacity: 0.12;
    pointer-events: none;
  }

  .organic-wrapper {
    position: relative;
    z-index: 1;
    max-width: 900px;
    margin: 0 auto;
    padding: 3rem 1.5rem 4rem;
  }

  /* Header */
  .organic-header {
    text-align: center;
    margin-bottom: 3.5rem;
    position: relative;
  }

  .organic-badge {
    display: inline-block;
    background: var(--autumn-green);
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 0.35rem 1.2rem;
    border-radius: 50px;
    margin-bottom: 1rem;
  }

  .organic-header h1 {
    font-size: clamp(2.5rem, 6vw, 4rem);
    font-weight: 700;
    color: var(--dark-green);
    line-height: 1.1;
    margin: 0 0 0.8rem;
    letter-spacing: -0.02em;
  }

  .organic-header h1 span {
    color: var(--russian-red);
    font-style: italic;
  }

  .organic-header p {
    color: #5a6b40;
    font-size: 1.1rem;
    font-style: italic;
    margin: 0;
  }

  .header-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .header-divider-line {
    height: 1px;
    width: 80px;
    background: linear-gradient(to right, transparent, var(--autumn-green));
  }

  .header-divider-line.right {
    background: linear-gradient(to left, transparent, var(--autumn-green));
  }

  .header-divider-icon {
    color: var(--citrusy-orange);
    font-size: 1.2rem;
  }

  /* Contact Cards */
  .contact-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.2rem;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 640px) {
    .contact-cards { grid-template-columns: 1fr; }
  }

  .contact-card {
    background: white;
    border: 1.5px solid #e8dfc8;
    border-radius: 16px;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .contact-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(to right, var(--autumn-green), var(--citrusy-orange));
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .contact-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(79,126,23,0.12);
    border-color: var(--edam);
  }

  .contact-card:hover::before { opacity: 1; }

  .card-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--light-edam);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.9rem;
  }

  .card-icon-wrap svg { color: var(--citrusy-orange); }

  .contact-card h3 {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--autumn-green);
    margin: 0 0 0.4rem;
  }

  .contact-card a, .contact-card p {
    font-size: 0.9rem;
    color: #3d3d3d;
    text-decoration: none;
    font-style: italic;
    margin: 0;
  }

  .contact-card a:hover {
    color: var(--russian-red);
    text-decoration: underline;
  }

  /* Main Form Section */
  .form-section {
    background: white;
    border: 1.5px solid #e8dfc8;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 4px 30px rgba(79,126,23,0.07);
  }

  .form-header {
    background: linear-gradient(135deg, var(--autumn-green) 0%, #3d6212 100%);
    padding: 1.8rem 2rem;
    position: relative;
    overflow: hidden;
  }

  .form-header::after {
    content: '';
    position: absolute;
    right: -30px; top: -30px;
    width: 120px; height: 120px;
    border-radius: 50%;
    background: rgba(255,255,255,0.06);
  }

  .form-header::before {
    content: '';
    position: absolute;
    right: 20px; bottom: -40px;
    width: 80px; height: 80px;
    border-radius: 50%;
    background: rgba(240,196,0,0.15);
  }

  .form-header h2 {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.3rem;
  }

  .form-header p {
    color: rgba(255,255,255,0.75);
    font-size: 0.9rem;
    font-style: italic;
    margin: 0;
  }

  .form-body {
    padding: 2rem;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.2rem;
  }

  @media (max-width: 580px) {
    .form-grid { grid-template-columns: 1fr; }
    .form-body { padding: 1.5rem; }
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .form-field.full { grid-column: 1 / -1; }

  .form-field label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--dark-green);
  }

  .form-field input,
  .form-field textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1.5px solid #ddd5c0;
    border-radius: 10px;
    font-family: inherit;
    font-size: 0.95rem;
    color: #2d2d2d;
    background: var(--cream);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    outline: none;
  }

  .form-field input:focus,
  .form-field textarea:focus {
    border-color: var(--autumn-green);
    box-shadow: 0 0 0 3px rgba(79,126,23,0.12);
    background: white;
  }

  .form-field input::placeholder,
  .form-field textarea::placeholder {
    color: #b5a98a;
    font-style: italic;
  }

  .form-field textarea {
    resize: none;
    min-height: 140px;
  }

  /* Submit Button */
  .submit-btn {
    width: 100%;
    padding: 0.9rem 2rem;
    background: linear-gradient(135deg, var(--russian-red) 0%, #a50201 100%);
    color: white;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    margin-top: 1.2rem;
    position: relative;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .submit-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--citrusy-orange), var(--russian-red));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(205,3,2,0.3);
  }

  .submit-btn:hover:not(:disabled)::before { opacity: 1; }

  .submit-btn span { position: relative; z-index: 1; }

  .submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Alerts */
  .alert {
    margin-top: 1.2rem;
    padding: 1rem 1.2rem;
    border-radius: 10px;
    font-size: 0.9rem;
  }

  .alert-success {
    background: #f0f7e8;
    border: 1px solid #c3dea0;
    color: var(--dark-green);
  }

  .alert-error {
    background: #fdf0f0;
    border: 1px solid #f0b8b8;
    color: #8b0000;
  }

  .alert strong { font-weight: 700; }

  /* Map Section */
  .map-section {
    margin-top: 2rem;
    background: white;
    border: 1.5px solid #e8dfc8;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  }

  .map-header {
    padding: 1.2rem 1.5rem;
    background: var(--light-edam);
    border-bottom: 1px solid #e8dfc8;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .map-pin-icon {
    width: 36px; height: 36px;
    background: var(--citrusy-orange);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .map-header h3 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--dark-green);
    margin: 0 0 0.15rem;
  }

  .map-header p {
    font-size: 0.82rem;
    color: #6b7a50;
    margin: 0;
    font-style: italic;
  }

  .map-frame { display: block; width: 100%; height: 380px; border: none; }

  .map-footer {
    padding: 1rem 1.5rem;
    background: var(--light-edam);
    border-top: 1px solid #e8dfc8;
  }

  .map-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--autumn-green);
    font-size: 0.88rem;
    font-weight: 700;
    text-decoration: none;
    transition: color 0.2s ease;
  }

  .map-link:hover { color: var(--russian-red); }

  /* About section */
  .about-section {
    margin-top: 2rem;
    background: linear-gradient(135deg, var(--dark-green) 0%, #3d6212 100%);
    border-radius: 20px;
    padding: 2.5rem;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .about-section::before {
    content: '🌿';
    position: absolute;
    font-size: 5rem;
    opacity: 0.08;
    left: -1rem; top: -1rem;
  }

  .about-section::after {
    content: '🌿';
    position: absolute;
    font-size: 5rem;
    opacity: 0.08;
    right: -1rem; bottom: -1rem;
    transform: scaleX(-1);
  }

  .about-section h3 {
    color: var(--sunburst);
    font-size: 1.6rem;
    font-weight: 700;
    margin: 0 0 1rem;
    letter-spacing: -0.01em;
  }

  .about-section p {
    color: rgba(255,255,255,0.85);
    font-size: 0.98rem;
    line-height: 1.75;
    max-width: 600px;
    margin: 0 auto;
    font-style: italic;
  }

  /* Loading */
  .loading-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--cream);
    font-family: 'Półtawski Nowy', 'Playfair Display', Georgia, serif;
  }

  .loading-inner { text-align: center; }

  .spinner {
    width: 48px; height: 48px;
    border: 3px solid var(--edam);
    border-top-color: var(--autumn-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .loading-inner p {
    color: var(--autumn-green);
    font-size: 1rem;
    font-style: italic;
  }

  /* Organic fruit icons as decorations */
  .organic-icons-row {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin: 1.5rem 0 0;
    font-size: 1.3rem;
    opacity: 0.7;
  }
`;

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const defaultContactInfo = {
    pageHeading: 'Contact Us',
    pageSubtitle: "We're here to help with any questions or comments",
    email: 'ebusiness.contact@gmail.com',
    phone: '+8809647001250',
    address: 'Aftabnagar, Dhaka, Bangladesh',
    googleMapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.8971203935644!2d90.42902931498165!3d23.753056884589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b85c740f17d1%3A0x8c3f9ba2dbfba9a8!2sAftabnagar%2C%20Dhaka!5e0!3m2!1sen!2sbd!4v1234567890123!5m2!1sen!2sbd',
    googleMapSearchQuery: 'Aftabnagar,Dhaka,Bangladesh',
    companyName: 'E-Business',
    companyDescription: "Your trusted online shopping platform. We are committed to providing high-quality products and the best customer service. Don't hesitate to contact us for any questions or assistance."
  };

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/contact-info');
        if (response.data.success) {
          setContactInfo(response.data.contactInfo);
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Failed to load contact info');
        setContactInfo(defaultContactInfo);
      } finally {
        setLoading(false);
      }
    };
    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    const apiUrl = 'https://bazar-wala-main-server-974485829900.asia-south1.run.app/contact';
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('যোগাযোগের ফর্ম সাবমিট করতে ত্রুটি:', error);
      setFormStatus('error');
    }
  };

  if (loading) {
    return (
      <>
        <style>{fontStyle}</style>
        <div className="loading-screen">
          <div className="loading-inner">
            <div className="spinner"></div>
            <p>Loading fresh content...</p>
          </div>
        </div>
      </>
    );
  }

  const info = contactInfo || defaultContactInfo;

  return (
    <>
      <style>{fontStyle}</style>
      <div className="organic-page">
        <div className="organic-wrapper">

          {/* Header */}
          <div className="organic-header">
            <div className="organic-badge">🌿 Fresh &amp; Natural</div>
            <h1>
              Get in <span>Touch</span>
            </h1>
            <p>{info.pageSubtitle}</p>
            <div className="header-divider">
              <div className="header-divider-line"></div>
              <span className="header-divider-icon">🍊</span>
              <div className="header-divider-line right"></div>
            </div>
            <div className="organic-icons-row">
              <span>🥦</span><span>🍅</span><span>🥕</span><span>🌽</span><span>🍋</span>
            </div>
          </div>

          {/* Contact Info Cards */}
          <div className="contact-cards">
            <div className="contact-card">
              <div className="card-icon-wrap">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
              </div>
              <h3>Email</h3>
              <a href={`mailto:${info.email}`}>{info.email}</a>
            </div>

            <div className="contact-card">
              <div className="card-icon-wrap">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.774a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                </svg>
              </div>
              <h3>Phone</h3>
              <a href={`tel:${info.phone}`}>{info.phone}</a>
            </div>

            <div className="contact-card">
              <div className="card-icon-wrap">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                </svg>
              </div>
              <h3>Address</h3>
              <p>{info.address}</p>
            </div>
          </div>

          {/* Message Form */}
          <div className="form-section">
            <div className="form-header">
              <h2>Send Us a Message</h2>
              <p>We'll respond within 24 hours — fresh and prompt!</p>
            </div>

            <div className="form-body">
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="name">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@email.com"
                  />
                </div>

                <div className="form-field full">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Enter subject"
                  />
                </div>

                <div className="form-field full">
                  <label htmlFor="message">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Write your message here..."
                  ></textarea>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={formStatus === 'submitting'}
                className="submit-btn"
              >
                <span>
                  {formStatus === 'submitting' ? '⏳ Sending...' : '🌿 Send Message'}
                </span>
              </button>

              {formStatus === 'success' && (
                <div className="alert alert-success">
                  <strong>✅ Success!</strong> Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="alert alert-error">
                  <strong>❌ Error!</strong> Failed to send message. Please try again.
                </div>
              )}
            </div>
          </div>

          {/* Google Map Section */}
          <div className="map-section">
            <div className="map-header">
              <div className="map-pin-icon">
                <svg width="18" height="18" fill="white" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div>
                <h3>Find Us Here</h3>
                <p>{info.address}</p>
              </div>
            </div>
            <iframe
              src={info.googleMapUrl}
              className="map-frame"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map"
            ></iframe>
            <div className="map-footer">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${info.googleMapSearchQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                </svg>
                View on Google Maps
              </a>
            </div>
          </div>

          {/* About Section */}
          <div className="about-section">
            <h3>{info.companyName}</h3>
            <p>{info.companyDescription}</p>
          </div>

        </div>
      </div>
    </>
  );
};

export default ContactUsPage;